import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useEscapeKey } from '../hooks/useEscapeKey';
import DatePicker from 'react-datepicker';
import { formatDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import "react-datepicker/dist/react-datepicker.css";
import { transactions, contacts, images } from '../utils/api';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const ViewPurchaseTransactionModal = ({ isOpen, onClose, transaction, onUpdate, handleDelete }) => {
  useEscapeKey(onClose, isOpen);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [contactsList, setContactsList] = useState([]);
  const [contactName, setContactName] = useState('');
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // Reset delete prompt when transaction changes
    setShowDeletePrompt(false);
    
    if (isEditing && transaction) {
      const coin_prices = {};
      transaction.coins?.forEach(coin => {
        coin_prices[coin.coin_id] = {
          price: coin.purchase_price || '',
          fees: coin.fees || ''
        };
      });

      setEditFormData({
        transaction_date: transaction.transaction_date ? new Date(transaction.transaction_date) : new Date().toISOString(),
        contact_id: transaction.contact_id || '',
        notes: transaction.notes || '',
        type: 'purchase',
        coin_prices
      });
    }
  }, [isEditing, transaction]);

  useEffect(() => {
    if (transaction?.contact_name) {
      setContactName(transaction.contact_name);
    } else if (transaction?.contact_id) {
      contacts.get(transaction.contact_id)
        .then(contact => {
          if (contact) {
            const name = contact.company_name || `${contact.first_name} ${contact.last_name}`;
            setContactName(name);
          }
        })
        .catch(err => console.error('Failed to fetch contact name', err));
    } else {
      setContactName('');
    }
  }, [transaction]);

  useEffect(() => {
    if (isEditing) {
      const fetchContacts = async () => {
        try {
          const response = await contacts.getAll();
          setContactsList(response || []);
        } catch (err) {
          console.error('Error fetching contacts:', err);
          setError('Failed to load contacts');
        }
      };
      fetchContacts();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setError(null);
    
    try {
      
      if (!editFormData.contact_id) {
        setError('Please select a contact');
        return;
      }

      const coin_prices = {};
      Object.entries(editFormData.coin_prices).forEach(([coin_id, data]) => {
        if (data.price !== undefined && data.price !== '') {
          coin_prices[coin_id] = {
            price: Number(data.price),
            fees: Number(data.fees || 0)
          };
        }
      });

      if (Object.keys(coin_prices).length === 0) {
        setError('Please enter at least one price');
        return;
      }

      const updatedData = {
        transaction_date: editFormData.transaction_date.toISOString(),
        contact_id: parseInt(editFormData.contact_id, 10),
        notes: editFormData.notes || '',
        type: 'purchase',
        coin_prices
      };
      
      await transactions.update(transaction.id, updatedData);
      if (onUpdate) await onUpdate();
      setIsEditing(false);
      setEditFormData(null);
    } catch (err) {
      console.error('Error updating transaction:', err);
      setError('Failed to update transaction');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCoinChange = (coinId, field, value) => {
    setEditFormData(prev => ({
      ...prev,
      coin_prices: {
        ...prev.coin_prices,
        [coinId]: {
          ...prev.coin_prices[coinId],
          [field === 'purchase_price' ? 'price' : field]: value
        }
      }
    }));
  };

  const calculateTotal = () => {
    if (isEditing && editFormData?.coin_prices) {
      return Object.values(editFormData.coin_prices).reduce((acc, coin) => 
        acc + Number(coin.price || 0), 0);
    }

    return transaction.coins?.reduce((acc, coin) => 
      acc + (coin.purchase_price || 0), 0);
  };

  if (!isOpen || !transaction) return null;

  const total = calculateTotal();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Purchase Details</h3>
            <div className="flex items-center space-x-2">
              {showDeletePrompt && (
                <DeleteConfirmationModal
                  isOpen={true}
                  title="Delete Transaction"
                  message="Are you sure you want to delete this transaction? This action cannot be undone."
                  onConfirm={async () => {
                    try {
                      await handleDelete(transaction.id);
                      onClose();
                    } catch (err) {
                      console.error('Error deleting transaction:', err);
                      setError('Failed to delete transaction');
                    }
                  }}
                  onCancel={() => setShowDeletePrompt(false)}
                />
              )}
              {isEditing ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(false);
                      setEditFormData(null);
                      setError(null);
                    }}
                    className="px-3 py-1.5 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="px-3 py-1.5 border border-transparent rounded shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSaving}
                  >
                    Save
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setIsEditing(true)} className="text-blue-600 hover:text-blue-800 p-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                  <button onClick={() => setShowDeletePrompt(true)} className="text-red-600 hover:text-red-800 p-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </>
              )}
              <div className="h-4 w-px bg-gray-300"></div>
              <button onClick={() => {
                setShowDeletePrompt(false);
                onClose();
              }} className="text-gray-400 hover:text-gray-500 p-1">
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          <div className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded">
                {error}
              </div>
            )}

            <div className="flex flex-col md:flex-row gap-6">
              <div className="md:w-1/3">
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-3 gap-2">
                  {transaction.coins?.map((coin) => (
                    <Link key={coin.coin_id} to={`/coins/${coin.coin_id}`} className="block">
                      {coin.thumbnail_path ? (
                        <img
                          src={images.getImage(coin.thumbnail_path, coin.source)}
                          alt={`${coin.country} ${coin.year} ${coin.denomination}`}
                          className="w-full aspect-square object-contain rounded border border-gray-200"
                        />
                      ) : (
                        <div className="w-full aspect-square rounded border border-gray-200 flex items-center justify-center bg-gray-50">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l-1.586-1.586a2 2 0 00-2.828 0L6 14m6-6l.01.01" />
                          </svg>
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="md:w-2/3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Transaction ID</p>
                    <p className="text-sm text-gray-900">{transaction.id}</p>
                  </div>
              
                  {isEditing && editFormData ? (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-500 block mb-1">Date</label>
                        <DatePicker
                          selected={editFormData.transaction_date}
                          onChange={date => setEditFormData(prev => ({ ...prev, transaction_date: date }))}
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 w-full sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 block mb-1">Contact</label>
                        <select
                          name="contact_id"
                          value={editFormData.contact_id}
                          onChange={handleChange}
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 w-full sm:text-sm border-gray-300 rounded-md"
                        >
                          <option value="">Select a contact</option>
                          {contactsList.map(contact => (
                            <option key={contact.id} value={contact.id}>
                              {contact.company_name || `${contact.first_name} ${contact.last_name}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Notes</label>
                        <textarea
                          name="notes"
                          value={editFormData.notes}
                          onChange={handleChange}
                          rows={2}
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 w-full sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Date</p>
                        <p className="text-sm text-gray-900">
                          {transaction.transaction_date ? formatDate(transaction.transaction_date, { user }) : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Contact</p>
                        <p className="text-sm text-gray-900">
                          {contactName ? (
                            <Link to={`/contacts/${transaction.contact_id}`} className="text-blue-600 hover:text-blue-800">
                              {contactName}
                            </Link>
                          ) : (
                            'No contact assigned'
                          )}
                        </p>
                      </div>
                      <div className="sm:col-span-2">
                        <p className="text-sm font-medium text-gray-500">Notes</p>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{transaction.notes || 'No notes'}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">ID</th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Purchase Price</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transaction.coins?.map((coin) => {
                    const description = [
                      coin.country,
                      coin.year,
                      coin.denomination,
                      coin.variety,
                      coin.grade,
                      coin.grade_company
                    ].filter(Boolean).join(' ');

                    const currentCoinData = editFormData?.coin_prices?.[coin.coin_id] || coin;

                    return (
                      <tr key={coin.coin_id} className="text-sm">
                        <td className="px-4 py-2 whitespace-nowrap">
                          <Link to={`/coins/${coin.coin_id}`} className="text-blue-600 hover:text-blue-800">
                            {coin.coin_id}
                          </Link>
                        </td>
                        <td className="px-4 py-2">{description}</td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          {isEditing ? (
                            <input
                              inputmode="decimal"
                              pattern="[0-9]*\.?[0-9]*"
                              step="0.01"
                              value={currentCoinData.price || ''}
                              onChange={(e) => handleCoinChange(coin.coin_id, 'purchase_price', e.target.value)}
                              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-24 sm:text-sm border-gray-300 rounded-md text-right"
                            />
                          ) : (
                            `$${Number(currentCoinData.price || currentCoinData.purchase_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="text-sm font-semibold bg-gray-50">
                    <td className="px-4 py-2 whitespace-nowrap" colSpan={2}>Total</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewPurchaseTransactionModal;
